import { useState } from 'react';
import { Info, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CapacityBar } from '@/components/dashboard/CapacityBar';
import { Panel } from '@/components/dashboard/Panel';
import { EndpointResourcesDialog, type ResourceValues } from '@/components/endpoints/EndpointResourcesDialog';
import { api, ApiError } from '@/lib/api';
import { formatGb } from '@/lib/format';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { CapacityUsage, EndpointResourceResponse } from '@/types/endpoint';

const pct = (used: number, total: number) => (total > 0 ? Math.round((used / total) * 100) : 0);

function Bars({ c }: { c: CapacityUsage }) {
  return (
    <div className="space-y-4">
      <CapacityBar label="CPU" allocated={c.cpu_used} available={c.cpu_total} percent={pct(c.cpu_used, c.cpu_total)} unit="cores" />
      <CapacityBar
        label="Memory"
        allocated={c.memory_used_mb}
        available={c.memory_total_mb}
        percent={pct(c.memory_used_mb, c.memory_total_mb)}
        unit="GB"
        format={formatGb}
      />
      <CapacityBar
        label="Storage"
        allocated={c.storage_used_gb}
        available={c.storage_total_gb}
        percent={pct(c.storage_used_gb, c.storage_total_gb)}
        unit="GB"
      />
      <p className="text-sm text-muted-foreground">Totals come from the host; usage is the sum of deployed app limits.</p>
    </div>
  );
}

export function CapacityPanel({ endpointId }: { endpointId: number }) {
  // The server answers 404 when no resource record exists yet; treat that as "no data yet" (null), not a failure.
  const { data, error, loading, refetch } = usePolledData<EndpointResourceResponse | null>(async () => {
    try {
      return await api.endpointResources.get(endpointId);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  }, 30_000);

  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const hasData = data !== undefined;
  const hasTotals = !!data?.capacity;
  const manual = !!data?.is_manual_override;

  const save = async (values: ResourceValues) => {
    await api.endpointResources.update(endpointId, values);
    toast.success('Resource values saved');
    refetch();
  };

  const reset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await api.endpointResources.reset(endpointId);
      toast.success('Switched back to automatic. Values refresh on the next sync.');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to reset resources');
    } finally {
      setResetting(false);
      setResetOpen(false);
      refetch();
    }
  };

  const initial: ResourceValues = {
    cpu_available: data?.cpu_available ?? 0,
    memory_available_mb: data?.memory_available_mb ?? 0,
    storage_available_gb: data?.storage_available_gb ?? 0,
  };

  return (
    <Panel
      title="Capacity"
      loading={loading}
      error={error}
      hasData={hasData}
      onRetry={refetch}
      action={
        hasData && !hasTotals ? (
          <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
            Edit
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {data?.capacity ? <Bars c={data.capacity} /> : null}

        {data?.capacity && data.capacity.cpu_total === 0 && data.capacity.memory_total_mb === 0 && data.capacity.storage_total_gb === 0 ? (
          <p className="text-sm text-muted-foreground">Host totals are 0 — the last sync couldn't read Docker info.</p>
        ) : null}

        {hasData && !hasTotals ? (
          <div className="space-y-3">
            <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                {data
                  ? "Host totals haven't been synced yet. The values below are for reference: new apps are only placed on endpoints with synced host totals."
                  : 'No resource values have been recorded for this endpoint yet.'}
              </span>
            </p>
            {data ? (
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground">CPU available</dt>
                  <dd className="tabular-nums font-medium">{data.cpu_available} cores</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Memory available</dt>
                  <dd className="tabular-nums font-medium">{data.memory_available_mb.toLocaleString()} MB</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Storage available</dt>
                  <dd className="tabular-nums font-medium">{data.storage_available_gb.toLocaleString()} GB</dd>
                </div>
              </dl>
            ) : null}
          </div>
        ) : null}

        {manual ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning">
            <span className="flex items-start gap-1.5">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>Manual override is on. Automatic resource sync is paused for this endpoint, so host totals stop refreshing.</span>
            </span>
            <Button variant="outline" size="sm" aria-disabled={resetting} onClick={() => { if (!resetting) setResetOpen(true); }}>
              Reset to automatic
            </Button>
          </div>
        ) : null}
      </div>

      <EndpointResourcesDialog open={editOpen} onClose={() => setEditOpen(false)} initial={initial} onSave={save} />
      <ConfirmDialog
        open={resetOpen}
        onOpenChange={(o) => {
          if (!o && !resetting) setResetOpen(false);
        }}
        title="Reset to automatic?"
        description="Clears the manual override so automatic resource sync resumes. The current values stay until the next sync."
        confirmLabel="Reset to automatic"
        loading={resetting}
        onConfirm={reset}
      />
    </Panel>
  );
}
